/**
 * FilterBuilder — "Filters" step of Combine & Filter (prototype v39
 * `#page-combine-filter`, `renderCombineFilterList` / `addCombineFilter`).
 *
 * UI-parity pass (2026-09-23, orchestrator addendum): quick-add buttons
 * ("+ In every list · + Title · + Company · + Location") add ONE ROW per
 * click, each row its own [Include/Exclude] [field] [op] [value] [×] line —
 * replacing the earlier always-visible three-clause-with-chip-list shape.
 * Several rows on the SAME field OR together into that field's
 * `CombineFilterClause.values` (mirrors the mock's `combineFilterGroups()`).
 * A row with no value is rendered dashed and contributes nothing to the
 * clause sent upstream — "ignored", not merely hidden. There is no
 * per-row mode: `CombineFilterClause.mode` is one value for the WHOLE
 * field (the contract has no per-value mode), so changing any row's mode
 * select changes every row of that field together.
 *
 * "In at least N lists" (task 2026-09-24-combine-more-filters; was "Only
 * people in every list") is a single accent row, added/removed as a unit by
 * its "+" button / its own ×, with a stepper over [2, source count] that
 * reads "Only people in every list" at the maximum — not a field with rows.
 * See `listRule.ts` for what it sends.
 *
 * Task 2026-09-24-combine-more-filters also adds six filters. Past company,
 * Past title and Keyword are text fields with rows, like Title / Company /
 * Location (Past company reuses the Company suggestions; Keyword matches
 * "contains" only, so it has no operator select). Time in current role,
 * Connection degree and Has email are one row each. Every text row counts
 * towards `MAX_VALUES_TOTAL` (30, the API's `_MAX_FILTER_VALUES_TOTAL`):
 * at the cap the text "+" buttons disable, with a hint. Like an empty text
 * row, a Time-in-role row with no bound (or bounds out of order) and a
 * Connection row with no degree ticked send nothing.
 *
 * State ownership: the list rule and the COMMITTED filters live in
 * `CombineFilterPage` (`filters` / `onChange`), same as before. The RAW rows (including an empty, just-added one, which has
 * nowhere to live in the committed `CombineFilter` shape) are this
 * component's own state, seeded once from `filters` on mount — the parent
 * resets it by remounting with a new `key` on "Start over"
 * (`CombineFilterPage`'s `resetKey`).
 *
 * Operator (round 2, sub-task R2-B): `op` — "contains" / "is" — is a
 * `<select>` now, not the static text it used to be, and (like `mode`) it is
 * ONE choice for the WHOLE field: `CombineFilterClause.op` has no per-value
 * shape, so changing any row's operator select changes every row of that
 * field together, with a small hint saying so. Defaults per the plan's
 * "Decisões de design" table: Title = contains, Company / Location = is.
 *
 * Candidacy stage (epic 2026-10-15-thrive-search-import, 05): one row, like
 * Connection, with a checkbox per stage: the ladder, the off-ladder stages
 * and "Unknown", each drawn as its `StagePill`. Its "+" button
 * exists only while a selected list carries Thrive candidacy data
 * (`ImportSummary.has_candidacy_data`); removing the last such list removes
 * the row. While a stage is ticked, each selected list WITHOUT candidacy data
 * is named in a warning under the row: its people have no stage, so they are
 * left out unless they are also in a list that has one. Its values do not
 * count towards `MAX_VALUES_TOTAL`.
 */
import { Minus, Plus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

// `CombineFilter` / `CombineFilterClause` come from the CLIENT module, not
// `@nu-assist/contracts` directly — round 2 (sub-task R2-B) locally extends
// the clause with `op` (see `imports.client.ts`'s own TODO(r2 merge) block).
import type { CombineFilterSuggestion } from "@nu-assist/contracts";

import type { CombineFilter, CombineFilterClause } from "../../../../clients/imports.client";
import { StagePill } from "../../../../components/stages/StagePill";
import {
  LADDER_STAGES,
  OFF_LADDER_STAGES,
  UNKNOWN_STAGE,
} from "../../../../components/stages/ladder";
import { InfoTip } from "../InfoTip";
import { type ComboboxSuggestion, SuggestionCombobox } from "./SuggestionCombobox";
import { type ListRule, MIN_LIST_RULE, applyListRule, stepListRule } from "./listRule";

/** `CombineFilterClause.values` cap — mirrors `schemas.py`'s `max_length=10`
 *  (iter-1 review, finding I6 / §6: the WAF's 8 KB body cap on `POST
 *  /combine` and `/combine/preview` has no headroom left once a filter's own
 *  values are unbounded — lowered from an effectively-uncapped 50 together
 *  with `MAX_VALUE_LENGTH` below). */
const MAX_VALUES_PER_CLAUSE = 10;
/** Per-value character cap — mirrors `schemas.py`'s `StringConstraints(max_length=60)`
 *  on each element of `CombineFilterClause.values`. */
const MAX_VALUE_LENGTH = 60;
/** Values across EVERY text field together — mirrors `schemas.py`'s
 *  `_MAX_FILTER_VALUES_TOTAL` (task 2026-09-24-combine-more-filters: six
 *  clauses at 10 values each would not fit the 7,900-byte body budget). */
export const MAX_VALUES_TOTAL = 30;
/** `CombineTenureClause`'s bounds, in months. */
const MAX_MONTHS = 600;

export interface FilterBuilderProps {
  filters: CombineFilter;
  onChange: (next: CombineFilter) => void;
  /** "In at least N lists" — see `listRule.ts`. */
  listRule: ListRule;
  onListRuleChange: (next: ListRule) => void;
  /** How many lists are currently selected — the list rule needs ≥ 2 to
   *  mean anything (product decision, epic README "Filters" row). */
  sourceCount: number;
  /** `CombinePreviewResponse.filter_suggestions` — offered by the Company,
   *  Past company (values only: the counts are of current companies) and
   *  Location comboboxes. */
  companyOptions: readonly CombineFilterSuggestion[];
  locationOptions: readonly CombineFilterSuggestion[];
  /** The selected lists, for the Candidacy stage filter: offered only when
   *  one of them carries Thrive candidacy data; the others are named in its
   *  warning. Omitted = none selected. */
  stageSources?: readonly StageSource[];
}

/** A selected list as the Candidacy stage filter sees it. */
export interface StageSource {
  id: string;
  title: string;
  /** `ImportSummary.has_candidacy_data`. */
  hasCandidacyData: boolean;
  /** `ImportSummary.total_contacts`. */
  people: number;
}

/** The stages the filter offers, in the API's `CANDIDACY_STAGE_FILTER_LABELS`
 *  order: the ladder, then the off-ladder stages (Rejected last), then
 *  "Unknown". */
export const CANDIDACY_FILTER_STAGES: readonly string[] = [
  ...LADDER_STAGES,
  ...OFF_LADDER_STAGES,
  UNKNOWN_STAGE,
];

type ClauseMode = CombineFilterClause["mode"];
type FieldKey = "title" | "company" | "location" | "past_company" | "past_title" | "keyword";
const FIELD_KEYS: readonly FieldKey[] = [
  "title",
  "company",
  "location",
  "past_company",
  "past_title",
  "keyword",
];
type HasEmail = NonNullable<CombineFilter["has_email"]>;
type Degree = 1 | 2 | 3;

/** Module-level, not per-instance: rows only need a REACT KEY unique enough
 *  to survive add/remove without misattributing an input's focus/value to
 *  the wrong row — an array-index key breaks that the moment a row before
 *  the edited one is removed. Shared across `FilterBuilder` instances is
 *  fine; uniqueness is all that is asked of it. */
let nextRowId = 0;
function newRowId(): number {
  nextRowId += 1;
  return nextRowId;
}

interface FieldRow {
  id: number;
  /** Empty until the recruiter types — a row the recruiter added or cleared
   *  but has not (yet) filled in. Filtered out before it ever reaches
   *  `onChange`. */
  value: string;
}

type ClauseOp = NonNullable<CombineFilterClause["op"]>;

interface FieldState {
  mode: ClauseMode;
  op: ClauseOp;
  rows: FieldRow[];
}

function initFieldState(
  fieldKey: FieldKey,
  clause: CombineFilterClause | null | undefined
): FieldState {
  return {
    mode: clause?.mode ?? "include",
    op: clause?.op ?? FIELD_DEFS[fieldKey].defaultOp,
    rows: (clause?.values ?? []).map((value) => ({ id: newRowId(), value })),
  };
}

/** `fixedOp`: the field has one operator only, so no select is shown. */
const FIELD_DEFS: Record<
  FieldKey,
  {
    label: string;
    addLabel: string;
    defaultOp: ClauseOp;
    placeholder: string;
    fixedOp?: boolean;
    /** The suggestion combobox's empty-state noun, when not the label's. */
    noun?: string;
  }
> = {
  title: {
    label: "Title",
    addLabel: "Title",
    defaultOp: "contains",
    placeholder: "e.g. VP, Director",
  },
  company: { label: "Company", addLabel: "Company", defaultOp: "is", placeholder: "e.g. Acme" },
  location: {
    label: "Location",
    addLabel: "Location",
    defaultOp: "is",
    placeholder: "e.g. New York",
  },
  past_company: {
    label: "Past company",
    addLabel: "Past company",
    defaultOp: "is",
    placeholder: "any job, e.g. Acme",
    noun: "company",
  },
  past_title: {
    label: "Past title",
    addLabel: "Past title",
    defaultOp: "contains",
    placeholder: "any job, e.g. VP Sales",
  },
  keyword: {
    label: "Keyword",
    addLabel: "Keyword",
    defaultOp: "contains",
    placeholder: "in summary or headline",
    fixedOp: true,
  },
};

const HAS_EMAIL_OPTIONS: readonly { value: HasEmail; label: string }[] = [
  { value: "any", label: "Has an email" },
  { value: "personal", label: "Has a personal email" },
  { value: "work", label: "Has a work email" },
  { value: "none", label: "Has no email" },
];

const DEGREE_LABEL: Record<Degree, string> = { 1: "1st", 2: "2nd", 3: "3rd" };

const ADD_BUTTON_CLASS =
  "rounded-md border border-border px-2.5 py-1 text-label text-text-secondary hover:border-border-hover disabled:opacity-40";
const SELECT_CLASS = "rounded-md border border-border bg-surface px-2 py-1 text-label text-text";

/** A Title / Past title / Keyword value input. Like `SuggestionCombobox`,
 *  typing is a draft: it reaches `onCommit` on Enter or when focus leaves
 *  the field (cleared text included), never per keystroke, so the preview
 *  and the funnel do not re-run on a half-typed value. Esc drops the draft. */
function CommitTextInput({
  value,
  onCommit,
  ...rest
}: {
  value: string;
  onCommit: (value: string) => void;
  placeholder: string;
  "aria-label": string;
  maxLength: number;
  "data-testid": string;
  className: string;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => {
    setDraft(value);
  }, [value]);
  function commit() {
    if (draft !== value) onCommit(draft);
  }
  return (
    <input
      type="text"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        } else if (e.key === "Escape" && draft !== value) {
          e.preventDefault();
          setDraft(value);
        }
      }}
      {...rest}
    />
  );
}

function FieldRows({
  fieldKey,
  state,
  options,
  onSetMode,
  onSetOp,
  onSetValue,
  onRemoveRow,
}: {
  fieldKey: FieldKey;
  state: FieldState;
  options?: readonly ComboboxSuggestion[];
  onSetMode: (mode: ClauseMode) => void;
  onSetOp: (op: ClauseOp) => void;
  onSetValue: (id: number, value: string) => void;
  onRemoveRow: (id: number) => void;
}) {
  const def = FIELD_DEFS[fieldKey];
  if (state.rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5" data-testid={`combine-filter-${fieldKey}`}>
      {/* One operator per FIELD, not per row — see this file's own docblock.
          Shown once, above the rows, so it reads as a property of the FIELD
          rather than something each row could disagree about. */}
      {state.rows.length > 1 && !def.fixedOp ? (
        <p
          className="text-caption text-text-muted"
          data-testid={`combine-filter-${fieldKey}-op-hint`}
        >
          One operator applies to every {def.label} row.
        </p>
      ) : null}
      {state.rows.map((row, i) => {
        const empty = !row.value.trim();
        return (
          <div
            // Keyed by the row's own stable id (not its position — see
            // `newRowId`), so removing an earlier row never misattributes a
            // later row's in-progress input value to the wrong DOM node.
            // `data-testid` still uses the POSITION: existing tests and
            // `FilterBuilder`'s own callers address a row by "the Nth one".
            key={row.id}
            data-testid={`combine-filter-${fieldKey}-row-${i}`}
            className={`flex flex-wrap items-center gap-2 rounded-md border px-2 py-1.5 ${
              empty ? "border-dashed border-border-hover" : "border-border"
            }`}
          >
            <select
              aria-label={`${def.label} mode`}
              value={state.mode}
              onChange={(e) => onSetMode(e.target.value as ClauseMode)}
              data-testid={`combine-filter-${fieldKey}-row-${i}-mode`}
              className="rounded-md border border-border bg-surface px-2 py-1 text-label text-text"
            >
              <option value="include">Include</option>
              <option value="exclude">Exclude</option>
            </select>
            <span className="min-w-14 text-label font-semibold text-text">{def.label}</span>
            {def.fixedOp ? (
              <span
                className="text-label text-text-secondary"
                data-testid={`combine-filter-${fieldKey}-row-${i}-op-fixed`}
              >
                contains
              </span>
            ) : (
              <select
                aria-label={`${def.label} operator`}
                value={state.op}
                onChange={(e) => onSetOp(e.target.value as ClauseOp)}
                data-testid={`combine-filter-${fieldKey}-row-${i}-op`}
                className={SELECT_CLASS}
              >
                <option value="contains">contains</option>
                <option value="is">is</option>
              </select>
            )}
            {options ? (
              <SuggestionCombobox
                value={row.value}
                onChange={(v) => onSetValue(row.id, v)}
                suggestions={options}
                // Values already on the field's OTHER rows are not offered again.
                exclude={
                  new Set(
                    state.rows
                      .filter((r) => r.id !== row.id && r.value.trim())
                      .map((r) => r.value.trim().toLocaleLowerCase())
                  )
                }
                noun={def.noun ?? def.label.toLocaleLowerCase()}
                label={`${def.label} value ${i + 1}`}
                placeholder={def.placeholder}
                maxLength={MAX_VALUE_LENGTH}
                testId={`combine-filter-${fieldKey}-row-${i}-input`}
              />
            ) : (
              <CommitTextInput
                value={row.value}
                onCommit={(v) => onSetValue(row.id, v)}
                placeholder={def.placeholder}
                aria-label={`${def.label} value ${i + 1}`}
                maxLength={MAX_VALUE_LENGTH}
                data-testid={`combine-filter-${fieldKey}-row-${i}-input`}
                className="min-w-32 flex-1 rounded-md border border-border bg-bg px-2 py-1.5 text-body text-text placeholder:text-text-muted focus:border-accent focus:outline-none"
              />
            )}
            <button
              type="button"
              onClick={() => onRemoveRow(row.id)}
              aria-label={`Remove ${def.label} row`}
              data-testid={`combine-filter-${fieldKey}-row-${i}-remove`}
              className="text-text-muted hover:text-text"
            >
              <X size={12} aria-hidden="true" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

function RemoveButton({
  label,
  testId,
  onClick,
}: { label: string; testId: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Remove ${label}`}
      data-testid={testId}
      className="text-text-muted hover:text-text"
    >
      <X size={12} aria-hidden="true" />
    </button>
  );
}

/** A months bound as typed: a whole number 0-600, `null` for blank, or
 *  `"invalid"` for anything else. */
function parseMonths(raw: string): number | null | "invalid" {
  const text = raw.trim();
  if (!text) return null;
  if (!/^\d+$/.test(text)) return "invalid";
  const n = Number(text);
  return n <= MAX_MONTHS ? n : "invalid";
}

interface TenureDraft {
  min: string;
  max: string;
}

/** The `months_in_role` a draft sends, and why it sends nothing when it
 *  is not usable (blank, out of range, or min above max). */
function tenureFromDraft(draft: TenureDraft): {
  clause: CombineFilter["months_in_role"];
  problem: string | null;
} {
  const min = parseMonths(draft.min);
  const max = parseMonths(draft.max);
  if (min === "invalid" || max === "invalid") {
    return { clause: null, problem: `Use whole months, 0-${MAX_MONTHS}.` };
  }
  if (min === null && max === null) return { clause: null, problem: null };
  if (min !== null && max !== null && min > max) {
    return { clause: null, problem: "The minimum is more than the maximum." };
  }
  return { clause: { min_months: min, max_months: max }, problem: null };
}

export function FilterBuilder({
  filters,
  onChange,
  listRule,
  onListRuleChange,
  sourceCount,
  companyOptions,
  locationOptions,
  stageSources = [],
}: FilterBuilderProps) {
  // Past company offers the company values WITHOUT their counts: a count of
  // people whose CURRENT company it is would misstate a past-job match.
  const pastCompanyOptions = useMemo(
    () => companyOptions.map(({ value }) => ({ value })),
    [companyOptions]
  );
  const [fields, setFields] = useState<Record<FieldKey, FieldState>>(() => {
    const init = {} as Record<FieldKey, FieldState>;
    for (const key of FIELD_KEYS) init[key] = initFieldState(key, filters[key]);
    return init;
  });
  // The single-row filters: `null` = no row. A row can be present and send
  // nothing (an empty Time-in-role draft, no degree ticked), like an empty
  // text row.
  const [tenure, setTenure] = useState<TenureDraft | null>(() =>
    filters.months_in_role
      ? {
          min: filters.months_in_role.min_months?.toString() ?? "",
          max: filters.months_in_role.max_months?.toString() ?? "",
        }
      : null
  );
  const [degrees, setDegrees] = useState<Degree[] | null>(
    () => (filters.connection_degrees as Degree[] | null | undefined) ?? null
  );
  const [hasEmail, setHasEmail] = useState<HasEmail | null>(() => filters.has_email ?? null);
  const [stages, setStages] = useState<string[] | null>(() => filters.candidacy_stages ?? null);
  const stageFilterOffered = stageSources.some((s) => s.hasCandidacyData);

  const totalRows = FIELD_KEYS.reduce((sum, key) => sum + fields[key].rows.length, 0);
  const atTotalCap = totalRows >= MAX_VALUES_TOTAL;

  function commit(field: FieldKey, next: FieldState) {
    setFields((prev) => ({ ...prev, [field]: next }));
    // Filter empty rows, dedupe case-insensitively (preserving order), cap
    // at MAX_VALUES_PER_CLAUSE — the row UI already blocks adding past the
    // cap (and past MAX_VALUES_TOTAL across fields), this is the defensive
    // mirror on the value the parent receives.
    const seen = new Set<string>();
    const values: string[] = [];
    for (const row of next.rows) {
      const v = row.value.trim();
      if (!v) continue;
      const key = v.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      values.push(v);
      if (values.length >= MAX_VALUES_PER_CLAUSE) break;
    }
    // Keyword's only operator is "contains"; the API refuses "is" there.
    const op = FIELD_DEFS[field].fixedOp ? FIELD_DEFS[field].defaultOp : next.op;
    onChange({
      ...filters,
      [field]: values.length > 0 ? { mode: next.mode, op, values } : null,
    });
  }

  function addRow(field: FieldKey) {
    const state = fields[field];
    if (state.rows.length >= MAX_VALUES_PER_CLAUSE || atTotalCap) return;
    commit(field, { ...state, rows: [...state.rows, { id: newRowId(), value: "" }] });
  }

  function setRowValue(field: FieldKey, id: number, value: string) {
    const state = fields[field];
    const rows = state.rows.map((row) =>
      row.id === id ? { ...row, value: value.slice(0, MAX_VALUE_LENGTH) } : row
    );
    commit(field, { ...state, rows });
  }

  function removeRow(field: FieldKey, id: number) {
    const state = fields[field];
    commit(field, { ...state, rows: state.rows.filter((row) => row.id !== id) });
  }

  function setMode(field: FieldKey, mode: ClauseMode) {
    commit(field, { ...fields[field], mode });
  }

  function setOp(field: FieldKey, op: ClauseOp) {
    commit(field, { ...fields[field], op });
  }

  function changeTenure(next: TenureDraft | null) {
    setTenure(next);
    onChange({ ...filters, months_in_role: next ? tenureFromDraft(next).clause : null });
  }

  function changeDegrees(next: Degree[] | null) {
    setDegrees(next);
    onChange({ ...filters, connection_degrees: next && next.length > 0 ? next : null });
  }

  function toggleDegree(degree: Degree) {
    const current = degrees ?? [];
    const next = current.includes(degree)
      ? current.filter((d) => d !== degree)
      : [...current, degree].sort();
    changeDegrees(next);
  }

  function changeHasEmail(next: HasEmail | null) {
    setHasEmail(next);
    onChange({ ...filters, has_email: next });
  }

  function changeStages(next: string[] | null) {
    setStages(next);
    onChange({ ...filters, candidacy_stages: next && next.length > 0 ? next : null });
  }

  function toggleStage(stage: string) {
    const current = stages ?? [];
    const ticked = current.includes(stage)
      ? current.filter((s) => s !== stage)
      : [...current, stage];
    changeStages(CANDIDACY_FILTER_STAGES.filter((s) => ticked.includes(s)));
  }

  // The last list with candidacy data was removed: the row goes with it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the offer ends, not per filter edit
  useEffect(() => {
    if (!stageFilterOffered && stages !== null) changeStages(null);
  }, [stageFilterOffered]);

  const applied = applyListRule(listRule, sourceCount);
  const listRuleDisabled = sourceCount < MIN_LIST_RULE || listRule !== null;
  const tenureProblem = tenure ? tenureFromDraft(tenure).problem : null;
  const tenureEmpty = tenure ? tenureFromDraft(tenure).clause == null : false;

  return (
    <div data-testid="combine-filter-builder" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onListRuleChange("all")}
          disabled={listRuleDisabled}
          title={
            sourceCount < MIN_LIST_RULE
              ? "Pick at least 2 people lists first"
              : listRule !== null
                ? "Already added"
                : "Only people who appear in at least N of the selected lists"
          }
          data-testid="combine-filter-add-overlap"
          className={ADD_BUTTON_CLASS}
        >
          + In at least N lists
        </button>
        {FIELD_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => addRow(key)}
            disabled={fields[key].rows.length >= MAX_VALUES_PER_CLAUSE || atTotalCap}
            data-testid={`combine-filter-add-${key}`}
            className={ADD_BUTTON_CLASS}
          >
            + {FIELD_DEFS[key].addLabel}
          </button>
        ))}
        <button
          type="button"
          onClick={() => changeTenure({ min: "", max: "" })}
          disabled={tenure !== null}
          data-testid="combine-filter-add-months"
          className={ADD_BUTTON_CLASS}
        >
          + Time in role
        </button>
        <button
          type="button"
          onClick={() => changeDegrees([])}
          disabled={degrees !== null}
          data-testid="combine-filter-add-degrees"
          className={ADD_BUTTON_CLASS}
        >
          + Connection
        </button>
        <button
          type="button"
          onClick={() => changeHasEmail("any")}
          disabled={hasEmail !== null}
          data-testid="combine-filter-add-email"
          className={ADD_BUTTON_CLASS}
        >
          + Email
        </button>
        {stageFilterOffered ? (
          <button
            type="button"
            onClick={() => changeStages([])}
            disabled={stages !== null}
            data-testid="combine-filter-add-stages"
            className={ADD_BUTTON_CLASS}
          >
            + Candidacy stage
          </button>
        ) : null}
      </div>
      {atTotalCap ? (
        <p className="text-caption text-text-muted" data-testid="combine-filter-total-cap">
          Up to {MAX_VALUES_TOTAL} filter values in total — remove one to add another.
        </p>
      ) : null}

      {/* Hidden below 2 sources even if still set: with one list the rule
          is a no-op and the page stops sending it (bug hunt 2026-09-23, F7). */}
      {applied.n !== null ? (
        <div
          className="flex items-center gap-2 rounded-md border border-accent bg-accent-muted px-3 py-1.5"
          data-testid="combine-filter-overlap-row"
        >
          <button
            type="button"
            onClick={() => onListRuleChange(stepListRule(listRule, -1, sourceCount))}
            disabled={applied.n <= MIN_LIST_RULE}
            aria-label="Fewer lists"
            data-testid="combine-filter-overlap-decrease"
            className="text-accent hover:opacity-80 disabled:opacity-40"
          >
            <Minus size={12} aria-hidden="true" />
          </button>
          <span
            className="flex-1 text-label font-semibold text-accent"
            data-testid="combine-filter-overlap-label"
          >
            {applied.requireAllSources
              ? "Only people in every list"
              : `In at least ${applied.n} of ${sourceCount} lists`}
          </span>
          <button
            type="button"
            onClick={() => onListRuleChange(stepListRule(listRule, 1, sourceCount))}
            disabled={applied.requireAllSources}
            aria-label="More lists"
            data-testid="combine-filter-overlap-increase"
            className="text-accent hover:opacity-80 disabled:opacity-40"
          >
            <Plus size={12} aria-hidden="true" />
          </button>
          <InfoTip content="Keeps only people who appear in at least this many of the selected lists. At the maximum, only people in every list." />
          <button
            type="button"
            onClick={() => onListRuleChange(null)}
            aria-label="Remove In at least N lists"
            data-testid="combine-filter-remove-overlap"
            className="text-accent hover:opacity-80"
          >
            <X size={12} aria-hidden="true" />
          </button>
        </div>
      ) : null}

      {FIELD_KEYS.map((key) => (
        <FieldRows
          key={key}
          fieldKey={key}
          state={fields[key]}
          options={
            key === "company"
              ? companyOptions
              : key === "past_company"
                ? pastCompanyOptions
                : key === "location"
                  ? locationOptions
                  : undefined
          }
          onSetMode={(mode) => setMode(key, mode)}
          onSetOp={(op) => setOp(key, op)}
          onSetValue={(id, v) => setRowValue(key, id, v)}
          onRemoveRow={(id) => removeRow(key, id)}
        />
      ))}

      {tenure ? (
        <div
          data-testid="combine-filter-months"
          className={`flex flex-wrap items-center gap-2 rounded-md border px-2 py-1.5 ${
            tenureEmpty ? "border-dashed border-border-hover" : "border-border"
          }`}
        >
          <span className="text-label font-semibold text-text">Time in current role</span>
          <input
            type="text"
            inputMode="numeric"
            value={tenure.min}
            onChange={(e) => changeTenure({ ...tenure, min: e.target.value })}
            placeholder="min"
            aria-label="Minimum months in current role"
            data-testid="combine-filter-months-min"
            className="w-16 rounded-md border border-border bg-bg px-2 py-1 text-body text-text placeholder:text-text-muted focus:border-accent focus:outline-none"
          />
          <span className="text-label text-text-secondary">to</span>
          <input
            type="text"
            inputMode="numeric"
            value={tenure.max}
            onChange={(e) => changeTenure({ ...tenure, max: e.target.value })}
            placeholder="max"
            aria-label="Maximum months in current role"
            data-testid="combine-filter-months-max"
            className="w-16 rounded-md border border-border bg-bg px-2 py-1 text-body text-text placeholder:text-text-muted focus:border-accent focus:outline-none"
          />
          <span className="text-label text-text-secondary">months</span>
          <InfoTip content="Whole months in the current job, counted to today. People with no current job (or no start date) are left out." />
          <RemoveButton
            label="Time in current role"
            testId="combine-filter-months-remove"
            onClick={() => changeTenure(null)}
          />
          {tenureProblem ? (
            <span
              className="w-full text-caption text-error"
              data-testid="combine-filter-months-error"
            >
              {tenureProblem}
            </span>
          ) : null}
        </div>
      ) : null}

      {degrees ? (
        <div
          data-testid="combine-filter-degrees"
          className={`flex flex-wrap items-center gap-2 rounded-md border px-2 py-1.5 ${
            degrees.length === 0 ? "border-dashed border-border-hover" : "border-border"
          }`}
        >
          <span className="text-label font-semibold text-text">Connection</span>
          {([1, 2, 3] as const).map((degree) => (
            <label key={degree} className="flex items-center gap-1 text-label text-text">
              <input
                type="checkbox"
                checked={degrees.includes(degree)}
                onChange={() => toggleDegree(degree)}
                data-testid={`combine-filter-degree-${degree}`}
              />
              {DEGREE_LABEL[degree]}
            </label>
          ))}
          <InfoTip content="Keeps people with one of the ticked LinkedIn connection degrees. People with no known degree are left out." />
          <RemoveButton
            label="Connection"
            testId="combine-filter-degrees-remove"
            onClick={() => changeDegrees(null)}
          />
        </div>
      ) : null}

      {hasEmail ? (
        <div
          data-testid="combine-filter-email"
          className="flex flex-wrap items-center gap-2 rounded-md border border-border px-2 py-1.5"
        >
          <select
            aria-label="Email"
            value={hasEmail}
            onChange={(e) => changeHasEmail(e.target.value as HasEmail)}
            data-testid="combine-filter-email-select"
            className={SELECT_CLASS}
          >
            {HAS_EMAIL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <InfoTip content="A personal email can come from another list the person is in; a work email is the kept row's own." />
          <RemoveButton
            label="Email"
            testId="combine-filter-email-remove"
            onClick={() => changeHasEmail(null)}
          />
        </div>
      ) : null}

      {stages && stageFilterOffered ? (
        <div
          data-testid="combine-filter-stages"
          className={`flex flex-col gap-1.5 rounded-md border px-2 py-1.5 ${
            stages.length === 0 ? "border-dashed border-border-hover" : "border-border"
          }`}
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-label font-semibold text-text">Candidacy stage</span>
            {CANDIDACY_FILTER_STAGES.map((stage) => (
              <label key={stage} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={stages.includes(stage)}
                  onChange={() => toggleStage(stage)}
                  data-testid={`combine-filter-stage-${stage}`}
                />
                <StagePill stage={stage} />
              </label>
            ))}
            <InfoTip content="Keeps people whose Thrive candidacy stage is one of the ticked ones. People without a stage, from lists that did not come from Thrive, are left out." />
            <RemoveButton
              label="Candidacy stage"
              testId="combine-filter-stages-remove"
              onClick={() => changeStages(null)}
            />
          </div>
          {stages.length > 0
            ? stageSources
                .filter((s) => !s.hasCandidacyData)
                .map((s) => (
                  <p
                    key={s.id}
                    className="text-caption text-warning-ink"
                    data-testid={`combine-filter-stages-warning-${s.id}`}
                  >
                    {s.title} did not come from Thrive: its {s.people.toLocaleString()}{" "}
                    {s.people === 1 ? "person has" : "people have"} no stage and{" "}
                    {s.people === 1 ? "is" : "are"} left out, unless also in a list that did.
                  </p>
                ))
            : null}
        </div>
      ) : null}
    </div>
  );
}
